// Generated from scripts/variant-picker.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { applyMainProduct } from '@theme/product-page';
import { getViewParameterValue, yieldToMainThread } from '@theme/utilities';
import { ProductSelectEvent, } from '@shopify/events';
/** Sections the picker asks Shopify to render for the product it sits in, in the order they are tried. */
const CARD_SECTION_ID = 'section-rendering-product-card';
const REQUEST_SECTIONS = ['quick-add-component', 'swatches-variant-picker-component', 'featured-product-information'];
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
/** The standard event's variant, which the section carries next to the picker as a view event payload. */
function selectedVariantOf(payload) {
    if (!payload)
        return null;
    try {
        const parsed = JSON.parse(payload);
        const product = isRecord(parsed) ? parsed.product : undefined;
        const variant = isRecord(product) ? product.selectedVariant : undefined;
        return isRecord(variant) ? variant : null;
    }
    catch {
        return null;
    }
}
/**
 * A variant picker. Its options are native radio inputs inside a fieldset per option, or a select per option; the
 * server renders their state, availability included. A change asks Shopify to render the product section for the new
 * selection (Section Rendering API with `option_values`), re-renders the picker from the answer and publishes the
 * standard `ProductSelectEvent`, whose promise settles with the rendered section so the price, media, inventory,
 * SKU, badges, buy buttons and sticky bar can follow without a request of their own.
 *
 * Contract kept from the inherited module: the `variant-picker` element, the `fieldsets[]` ref, the public methods
 * (`variantChanged`, `updateSelectedOption`, `buildRequestUrl`, `fetchUpdatedSection`, `updateVariantPicker`,
 * `updateElement`, `updateMain` and the selection getters), which the product card's swatch picker extends, and the
 * event payload and timing: the event is dispatched synchronously with the change, its promise resolves after the
 * morph with `{ variant, detail: { html, productId, newProduct, sourceId, resource } }`.
 *
 * An option value that cannot be bought stays selectable, so the shopper can read why: the server marks it with
 * `data-option-available="false"` and text, and the section answer carries the add to cart state.
 */
export default class VariantPicker extends Component {
    /** The product URL of the request in flight, so a quick second change on a combined listing keeps its target. */
    #pendingRequestUrl;
    #abortController;
    #lifetime;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        // Dispatch through the method so a subclass that overrides it, such as the card swatches, handles the change.
        this.addEventListener('change', (event) => this.variantChanged(event), { signal: lifetime.signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
        // A request that lands after the picker left the page has nothing to update.
        this.#abortController?.abort();
        this.#abortController = undefined;
    }
    /** Handles a change of an option: marks it selected, asks for the section, and keeps the address in step. */
    variantChanged(event) {
        const { target } = event;
        if (!(target instanceof HTMLElement))
            return;
        const selectedOption = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;
        if (!selectedOption)
            return;
        this.updateSelectedOption(target);
        const isOnProductPage = this.dataset.templateProductMatch === 'true' && !target.closest('product-card') && !target.closest('quick-add-dialog');
        // Morph the entire main content for combined listings child products, because changing the product
        // might also change other sections depending on recommendations, metafields, etc.
        const currentUrl = this.dataset.productUrl?.split('?')[0];
        const newUrl = selectedOption.dataset.connectedProductUrl;
        const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
        const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
        const morphElementSelector = loadsNewProduct ? 'main' : isOnFeaturedProductSection ? 'featured-product-information' : undefined;
        const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
        const { optionName } = selectedOption.dataset;
        const value = selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement ? selectedOption.value : '';
        this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
            morphElementSelector,
            detail: { optionValueId, variantId, connectedProductUrl },
            announce: optionName ? { name: optionName, value } : undefined,
        });
        const url = new URL(window.location.href);
        if (isOnProductPage) {
            if (variantId)
                url.searchParams.set('variant', variantId);
            else
                url.searchParams.delete('variant');
        }
        // Change the path if the option is connected to another product via combined listing.
        if (loadsNewProduct)
            url.pathname = newUrl;
        if (url.href !== window.location.href) {
            void yieldToMainThread().then(() => {
                history.replaceState({}, '', url.toString());
            });
        }
    }
    /**
     * Marks an option selected before its section arrives, so the controls and the legend answer at once.
     * `target` is the option's input or select, or the option value id of an input or option.
     */
    updateSelectedOption(target) {
        let element;
        if (typeof target === 'string') {
            const found = this.querySelector(`[data-option-value-id="${CSS.escape(target)}"]`);
            if (!found)
                throw new Error('Target element not found');
            element = found;
        }
        else {
            element = target;
        }
        if (element instanceof HTMLInputElement) {
            element.checked = true;
            this.#syncLegend(element);
        }
        if (element instanceof HTMLSelectElement) {
            const chosen = Array.from(element.options).find((option) => option.value === element.value);
            if (!chosen)
                throw new Error('Option not found');
            for (const option of element.options)
                option.removeAttribute('selected');
            chosen.setAttribute('selected', 'selected');
        }
    }
    /** Shows the chosen value, and why it cannot be bought if it cannot, in the legend of the option's fieldset. */
    #syncLegend(input) {
        const fieldset = input.closest('fieldset');
        if (!fieldset)
            return;
        const value = fieldset.querySelector('[data-selected-value]');
        if (value)
            value.textContent = input.value;
        const state = fieldset.querySelector('[data-selected-state]');
        if (state)
            state.textContent = input.closest('label')?.querySelector('[data-state]')?.textContent?.trim() ?? '';
    }
    /**
     * Builds the Section Rendering API URL for the current selection.
     * @param selectedOption the option the shopper chose
     * @param source set to `product-card` when the card's swatch picker asks
     * @param sourceSelectedOptionsValues the option value ids the card's swatch picker asks for
     */
    buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
        // The product URL and the pending request URL support combined listings: a shopper who changes options quickly
        // across products that live at separate URLs keeps asking the product the previous change chose.
        let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl || '';
        this.#pendingRequestUrl = productUrl;
        const params = [];
        const viewParamValue = getViewParameterValue();
        // Keep the view parameter, if it exists, for alternative product view testing.
        if (viewParamValue)
            params.push(`view=${viewParamValue}`);
        const selectedValues = this.selectedOptionsValues;
        if (selectedValues.length && !source) {
            params.push(`option_values=${selectedValues.join(',')}`);
        }
        else if (source === 'product-card') {
            if (selectedValues.length)
                params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
            else
                params.push(`option_values=${selectedOption.dataset.optionValueId}`);
        }
        // Inside some sections the request names the section to render.
        const closestSection = REQUEST_SECTIONS.find((selector) => this.closest(selector));
        if (closestSection) {
            if (productUrl.includes('?'))
                productUrl = productUrl.split('?')[0] ?? productUrl;
            const sectionId = closestSection === 'featured-product-information' ? this.closest(closestSection)?.id : CARD_SECTION_ID;
            return `${productUrl}?section_id=${sectionId}&${params.join('&')}`;
        }
        return `${productUrl}?${params.join('&')}`;
    }
    /**
     * Publishes the product select event and asks Shopify to render the product section for the current selection.
     * A newer call aborts the request of an older one; the older event's promise then rejects with an `AbortError`.
     */
    fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {}, announce } = {}) {
        const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
        this.#abortController?.abort();
        const controller = new AbortController();
        this.#abortController = controller;
        const deferred = ProductSelectEvent.createPromise();
        const productId = () => this.dataset.productId ?? '';
        this.dispatchEvent(new ProductSelectEvent({
            product: {
                id: productId(),
                title: this.dataset.productTitle ?? '',
                handle: this.dataset.productHandle ?? '',
            },
            selectedOptions: this.getAllSelectedOptions(),
            detail: { optionValueId, variantId, connectedProductUrl },
            promise: deferred.promise,
        }));
        fetch(requestUrl, { signal: controller.signal })
            .then((response) => {
            if (!response.ok)
                throw new Error(`The product section request failed with status ${response.status}.`);
            return response.text();
        })
            .then((responseText) => {
            this.#pendingRequestUrl = undefined;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            // Defer is only useful for the initial rendering of the page. Remove it here.
            html.querySelector('overflow-list[defer]')?.removeAttribute('defer');
            const variantPickerJsonScript = html.querySelector('variant-picker script[type="application/json"]');
            const textContent = variantPickerJsonScript?.textContent;
            if (!textContent) {
                deferred.resolve({
                    variant: null,
                    detail: { html, productId: productId(), sourceId: this.selectedOptionId, resource: null },
                });
                return;
            }
            let newProduct;
            if (morphElementSelector === 'main') {
                this.updateMain(html);
            }
            else if (morphElementSelector) {
                this.updateElement(html, morphElementSelector);
            }
            else {
                const { overflowList } = this.refs;
                const wasSwatchesExpanded = overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';
                newProduct = this.updateVariantPicker(html);
                if (wasSwatchesExpanded && overflowList instanceof OverflowList)
                    overflowList.showAll();
            }
            // Settle the event's promise with everything its listeners read.
            const sourceId = this.selectedOptionId;
            let resource = null;
            let variant = null;
            if (sourceId) {
                const variantData = JSON.parse(textContent);
                if (isRecord(variantData)) {
                    resource = variantData;
                    variant = selectedVariantOf(variantPickerJsonScript.closest('[view-event-payload]')?.getAttribute('view-event-payload')?.trim());
                }
            }
            // A null resource is a selection without a variant (an unavailable combination): the listeners still get the
            // section so they can update their state.
            const result = {
                variant,
                detail: { html, productId: productId(), newProduct, sourceId, resource },
            };
            this.#announce(announce, resource);
            deferred.resolve(result);
        })
            .catch((error) => {
            deferred.reject(error);
            if (!isAbort(error))
                console.error(error);
        });
    }
    /**
     * Says in the picker's polite live region what the shopper chose and, when the selection cannot be bought, why.
     * The server writes the two state words into the region, so the words follow the shopper's language.
     */
    #announce(announcement, resource) {
        if (!announcement || !this.isConnected)
            return;
        const region = this.querySelector('[ref="liveRegion"]');
        if (!region)
            return;
        const state = resource === null ? region.dataset.unavailableLabel : resource.available === false ? region.dataset.soldOutLabel : undefined;
        region.textContent = [`${announcement.name}: ${announcement.value}`, state].filter(Boolean).join(', ');
    }
    /**
     * Re-renders the variant picker from a document that holds the picker's new markup.
     * @returns the new product when a combined listing selection changed it
     */
    updateVariantPicker(newHtml) {
        let newProduct;
        const source = newHtml.querySelector(this.tagName.toLowerCase());
        if (!source)
            throw new Error('No new variant picker source found');
        // For combined listings, the product might have changed, so update the related data attributes.
        if (source instanceof HTMLElement) {
            const { productId, productUrl } = source.dataset;
            if (productId && productUrl && this.dataset.productId !== productId)
                newProduct = { id: productId, url: productUrl };
            if (productId !== undefined)
                this.dataset.productId = productId;
            if (productUrl !== undefined)
                this.dataset.productUrl = productUrl;
        }
        morph(this, source, {
            ...MORPH_OPTIONS,
            // Nodes that appear and disappear with an option value's state carry a key, so they never morph into each other.
            getNodeKey: (node) => (node instanceof HTMLElement ? node.dataset.key : undefined),
        });
        return newProduct;
    }
    /** Re-renders the element `elementSelector` names, such as the featured product, from the answer. */
    updateElement(newHtml, elementSelector) {
        const element = this.closest(elementSelector);
        const newElement = newHtml.querySelector(elementSelector);
        if (!element || !newElement)
            throw new Error(`No new element source found for ${elementSelector}`);
        morph(element, newElement);
    }
    /** Re-renders the entire main content, for a combined listing selection that moved to another product. */
    updateMain(newHtml) {
        applyMainProduct(newHtml);
    }
    /** The first selected option: a checked radio of a fieldset or the chosen option of a select. */
    get selectedOption() {
        const selected = this.querySelector('select option:checked, fieldset input:checked');
        return selected instanceof HTMLInputElement || selected instanceof HTMLOptionElement ? selected : undefined;
    }
    /** Every selected option as the name and value the standard event describes. */
    getAllSelectedOptions() {
        const options = [];
        // A select reports its current choice through `selectedOptions`; the `selected` attribute is only the initial state.
        for (const select of this.querySelectorAll('select')) {
            const selected = select.selectedOptions[0];
            if (selected?.dataset.optionName)
                options.push({ name: selected.dataset.optionName, value: selected.value });
        }
        for (const input of this.querySelectorAll('fieldset input:checked')) {
            if (input.dataset.optionName)
                options.push({ name: input.dataset.optionName, value: input.value });
        }
        return options;
    }
    /** The option value id of the first selected option. */
    get selectedOptionId() {
        const { selectedOption } = this;
        if (!selectedOption)
            return undefined;
        const { optionValueId } = selectedOption.dataset;
        if (!optionValueId)
            throw new Error('No option value ID found');
        return optionValueId;
    }
    /** The option value ids of every selected option, which the Section Rendering API takes as `option_values`. */
    get selectedOptionsValues() {
        return Array.from(this.querySelectorAll('select option:checked, fieldset input:checked')).map((option) => {
            const { optionValueId } = option.dataset;
            if (!optionValueId)
                throw new Error('No option value ID found');
            return optionValueId;
        });
    }
}
if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', VariantPicker);
}
